/** /settings/ai — per-feature AI routing table. One row per feature
 *  in FEATURE_CATALOG, three inline-editable knobs (provider, model,
 *  think). Backed by the feature_configs columns added in migration
 *  0148; saves go to /api/settings/ai/feature-configs/:featureId.
 *
 *  Banner at the top mirrors WakiAIService's /api/providers (proxied
 *  via /api/settings/ai/providers-status) so the operator can see at a
 *  glance whether each provider is ready (status=ok), needs a key
 *  (no_key) or is degraded. The default-of-defaults across the table
 *  is fireworks + accounts/fireworks/models/gpt-oss-120b; Waqas drops
 *  the key into ~/Library/Application Support/wakiai/secrets.json and
 *  the banner flips to ok without any redeploy.
 *
 *  Existing prompt-and-temperature editor lives at
 *  /settings/ai/features. That page is the long-form per-prompt
 *  editor; this page is the wide table for routing decisions. */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Save, RefreshCw } from "lucide-react";
import { getCsrfToken } from "../lib/auth";
import { TeachingBanner } from "../components/TeachingBanner";

type CatalogDefault = {
  provider: string;
  model: string;
  description?: string;
};

type FeatureConfigRow = {
  featureId: string;
  systemPrompt: string | null;
  modelPreference: string | null;
  think: boolean | null;
  temperature: number | null;
  maxTokens: number | null;
  scope: "private" | "public" | "either";
  provider: string;
  model: string;
  updatedAt: string;
  updatedBy: string | null;
};

type FeatureConfigEntry = {
  featureId: string;
  name: string;
  category: string;
  catalogDefault: CatalogDefault;
  config: FeatureConfigRow | null;
};

type ProviderStatus = {
  name: string;
  route: "local" | "cloud";
  status: "ok" | "no_key" | "degraded";
  reason: string | null;
  default_model: string;
  available_models: string[];
  last_call_ms: number | null;
};

type RowDraft = {
  provider: string;
  model: string;
  think: "" | "true" | "false";
};

const PROVIDER_OPTIONS: { value: string; label: string }[] = [
  { value: "fireworks", label: "Fireworks" },
  { value: "groq", label: "Groq" },
  { value: "local", label: "Local (Ollama)" },
];

const THINK_OPTIONS: { value: RowDraft["think"]; label: string }[] = [
  { value: "", label: "default" },
  { value: "true", label: "on" },
  { value: "false", label: "off" },
];

const DEFAULT_PROVIDER = "fireworks";
const DEFAULT_MODEL = "accounts/fireworks/models/gpt-oss-120b";

function configToDraft(c: FeatureConfigRow | null): RowDraft {
  return {
    provider: c?.provider ?? DEFAULT_PROVIDER,
    model: c?.model ?? DEFAULT_MODEL,
    think: c?.think == null ? "" : c.think ? "true" : "false",
  };
}

function providerColor(status: ProviderStatus["status"]): string {
  switch (status) {
    case "ok":
      return "border-emerald-400 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-200";
    case "no_key":
      return "border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200";
    case "degraded":
    default:
      return "border-rose-400 bg-rose-50 text-rose-800 dark:border-rose-700 dark:bg-rose-950 dark:text-rose-200";
  }
}

export function SettingsAi() {
  const [entries, setEntries] = useState<FeatureConfigEntry[]>([]);
  const [providers, setProviders] = useState<ProviderStatus[]>([]);
  const [providersErr, setProvidersErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, RowDraft>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Record<string, number>>({});
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);

  async function loadEntries(): Promise<void> {
    try {
      const res = await fetch("/api/settings/ai/feature-configs", {
        credentials: "same-origin",
        cache: "no-store",
      });
      const body = (await res.json()) as { ok: boolean; configs?: FeatureConfigEntry[]; error?: string };
      if (!body.ok || !body.configs) {
        setLoadErr(body.error ?? "failed to load feature configs");
        return;
      }
      const fresh: Record<string, RowDraft> = {};
      for (const e of body.configs) {
        fresh[e.featureId] = configToDraft(e.config);
      }
      setEntries(body.configs);
      setDraft(fresh);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function loadProviders(): Promise<void> {
    try {
      const res = await fetch("/api/settings/ai/providers-status", {
        credentials: "same-origin",
        cache: "no-store",
      });
      const body = (await res.json()) as { ok: boolean; providers?: ProviderStatus[]; error?: string };
      setProviders(Array.isArray(body.providers) ? body.providers : []);
      setProvidersErr(body.error ?? null);
    } catch (e) {
      setProvidersErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void (async () => {
      setLoading(true);
      await Promise.all([loadEntries(), loadProviders()]);
      setLoading(false);
    })();
  }, []);

  const byCategory = useMemo(() => {
    const out = new Map<string, FeatureConfigEntry[]>();
    for (const e of entries) {
      const list = out.get(e.category) ?? [];
      list.push(e);
      out.set(e.category, list);
    }
    return Array.from(out.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [entries]);

  async function save(featureId: string): Promise<void> {
    const d = draft[featureId];
    if (!d) return;
    setSaving(featureId);
    setRowErr({ ...rowErr, [featureId]: null });
    try {
      const body = {
        provider: d.provider,
        model: d.model,
        think: d.think === "" ? null : d.think === "true",
      };
      const res = await fetch(`/api/settings/ai/feature-configs/${encodeURIComponent(featureId)}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() ?? "" },
        body: JSON.stringify(body),
      });
      const out = (await res.json()) as { ok: boolean; error?: string; config?: FeatureConfigRow };
      if (!res.ok || !out.ok) {
        setRowErr({ ...rowErr, [featureId]: out.error ?? `save failed (${res.status})` });
        return;
      }
      setSavedAt({ ...savedAt, [featureId]: Date.now() });
      // Refresh entries so the row updatedAt + persisted values reflect the latest.
      await loadEntries();
    } catch (e) {
      setRowErr({ ...rowErr, [featureId]: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(null);
    }
  }

  // Banner: prefer the first non-ok provider to surface in the banner;
  // if everything is ok, show a single confirmation line.
  const banner = useMemo(() => {
    if (providersErr) {
      return {
        kind: "error" as const,
        text: `WakiAIService unreachable (${providersErr}). Provider status is unknown until the local service is back.`,
      };
    }
    const fireworks = providers.find((p) => p.name === "fireworks");
    if (fireworks?.status === "no_key") {
      return {
        kind: "warn" as const,
        text: 'Add your Fireworks key to ~/Library/Application Support/wakiai/secrets.json to enable cloud routing. See docs/SETUP_FIREWORKS.md.',
      };
    }
    const degraded = providers.filter((p) => p.status === "degraded");
    if (degraded.length > 0) {
      return {
        kind: "error" as const,
        text: `Provider degraded: ${degraded.map((d) => `${d.name} (${d.reason ?? "no reason"})`).join(", ")}`,
      };
    }
    return { kind: "ok" as const, text: "All providers ready." };
  }, [providers, providersErr]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">AI routing</h1>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Pick the provider and model for each Memso feature. Defaults to{" "}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px] dark:bg-slate-800">fireworks</code> /{" "}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px] dark:bg-slate-800">gpt-oss-120b</code>.
            Long-form prompt + temperature editing lives at{" "}
            <Link to="/settings/ai/features" className="underline">/settings/ai/features</Link>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            void loadEntries();
            void loadProviders();
          }}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </header>

      <TeachingBanner id="settings_ai_overview" />

      {/* Status banner. Always present so the operator never edits a
          table backed by an unreachable WakiAIService without noticing. */}
      <section
        data-testid="settings-ai-banner"
        className={`rounded-md border px-3 py-2 text-xs ${
          banner.kind === "ok"
            ? "border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100"
            : banner.kind === "warn"
              ? "border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
              : "border-rose-400 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100"
        }`}
      >
        {banner.text}
      </section>

      {/* Provider chips. Compact summary of every provider WakiAIService
          knows about, with the live status, last call latency, and the
          configured default model. */}
      <section className="flex flex-wrap gap-2">
        {providers.length === 0 && !loading && (
          <span className="text-xs text-slate-500">No providers reported.</span>
        )}
        {providers.map((p) => (
          <div
            key={p.name}
            className={`rounded-md border px-2 py-1 text-[11px] ${providerColor(p.status)}`}
          >
            <span className="font-medium">{p.name}</span>
            <span className="mx-1 opacity-60">·</span>
            <span>{p.status}</span>
            {p.last_call_ms != null && (
              <>
                <span className="mx-1 opacity-60">·</span>
                <span>{p.last_call_ms} ms</span>
              </>
            )}
          </div>
        ))}
      </section>

      {loadErr && (
        <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-700 dark:bg-rose-950 dark:text-rose-200">
          Failed to load feature configs: {loadErr}
        </div>
      )}

      {loading && <p className="text-xs text-slate-500">Loading...</p>}

      {byCategory.map(([category, items]) => (
        <section key={category} className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
            {category}
          </h2>
          <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-800">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2">Feature</th>
                  <th className="px-3 py-2">Provider</th>
                  <th className="px-3 py-2">Model</th>
                  <th className="px-3 py-2">Think</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => {
                  const d = draft[e.featureId];
                  if (!d) return null;
                  const isSaving = saving === e.featureId;
                  const err = rowErr[e.featureId];
                  const savedRecently = savedAt[e.featureId] && Date.now() - savedAt[e.featureId] < 5000;
                  const providerInfo = providers.find((p) => p.name === d.provider);
                  return (
                    <tr
                      key={e.featureId}
                      className="border-t border-slate-200 align-top dark:border-slate-800"
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900 dark:text-slate-100">{e.name}</div>
                        <div className="text-[10px] text-slate-500">{e.featureId}</div>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={d.provider}
                          onChange={(ev) =>
                            setDraft({
                              ...draft,
                              [e.featureId]: { ...d, provider: ev.target.value },
                            })
                          }
                          className="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-xs dark:border-slate-700 dark:bg-slate-950"
                        >
                          {PROVIDER_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          list={`models-${d.provider}`}
                          value={d.model}
                          onChange={(ev) =>
                            setDraft({
                              ...draft,
                              [e.featureId]: { ...d, model: ev.target.value },
                            })
                          }
                          placeholder={DEFAULT_MODEL}
                          className="w-full rounded border border-slate-300 bg-white px-1.5 py-1 font-mono text-[11px] dark:border-slate-700 dark:bg-slate-950"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={d.think}
                          onChange={(ev) =>
                            setDraft({
                              ...draft,
                              [e.featureId]: {
                                ...d,
                                think: ev.target.value as RowDraft["think"],
                              },
                            })
                          }
                          className="rounded border border-slate-300 bg-white px-1.5 py-1 text-xs dark:border-slate-700 dark:bg-slate-950"
                        >
                          {THINK_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => void save(e.featureId)}
                          disabled={isSaving}
                          className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                        >
                          <Save className="h-3 w-3" />
                          {isSaving ? "Saving" : savedRecently ? "Saved" : "Save"}
                        </button>
                        {err && (
                          <div className="mt-1 text-right text-[10px] text-rose-700 dark:text-rose-300">
                            {err}
                          </div>
                        )}
                      </td>
                      {/* datalist provides autocomplete from the provider's
                          available_models without forcing a strict dropdown,
                          so an operator can paste a custom model id too. */}
                      <td className="hidden">
                        <datalist id={`models-${d.provider}`}>
                          {(providerInfo?.available_models ?? []).map((m) => (
                            <option key={m} value={m} />
                          ))}
                        </datalist>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <footer className="pt-4 text-[11px] text-slate-500 dark:text-slate-400">
        Saving rewrites <code className="font-mono">feature_configs.provider</code> +{" "}
        <code className="font-mono">feature_configs.model</code>. The router consults these on
        the next call.
      </footer>
    </div>
  );
}
